import type {
  WorksiteDto, WorksiteHistoryEntryDto, WorksiteInput, WorksiteTeamCountDto,
} from '../../../api/hr-worksite';

/**
 * 통합설정 › 사업장 v3 (2026-09-14 · 사용자 결정 · 시안 `axi-settings-worksites` v3 · `axi-settings-worksite-detail` v2) — 목록 · 상세가 같이 쓰는 규칙.
 *
 * ★ 본사는 **회사정보가 정본**이다(2026-08-30 사용자 지적) — 목록에 본사 행을 두지 않고 안내 한 줄로 회사정보에 보낸다.
 * ★ 서버 v3 칸(담당자 · 사업자등록번호 · 종사업장 번호 · 휴일 나라 · 팀 집계 · 수정자 · 이력)은 서버 반영 전엔 **안 온다**(undefined) —
 *   그때 화면은 칸을 지우지 않고 「API 필요」 자리표시로 그린다(CLAUDE.md 「데이터가 없어도 디자인은 그린다」).
 * ★ 주소 찾기 = **입력만**(사용자 결정 (b)) — 단추는 두되 「추후 지원」 으로 잠근다(폐쇄망 · 나중에 내장 주소 DB 자리).
 */

export const WORKSITE_LIST_PATH = '/settings?pane=worksite';
export const WORKSITE_NEW_ID = 'new';
export const COMPANY_INFO_PATH = '/settings?pane=company';
export const worksitePath = (id: string): string => `/settings/worksites/${encodeURIComponent(id)}`;

export const API_NEEDED = 'API 필요';
export const LATER = '추후 지원';
export const FOLLOWUP = '후속';

export type WsFilter = 'all' | 'active' | 'inactive' | 'noAddress';

export const hasAddress = (s: Pick<WorksiteDto, 'address'>): boolean => !!s.address?.trim();

export function countFilters(sites: WorksiteDto[]): Record<WsFilter, number> {
  return {
    all: sites.length,
    active: sites.filter((s) => s.isActive).length,
    inactive: sites.filter((s) => !s.isActive).length,
    noAddress: sites.filter((s) => !hasAddress(s)).length,
  };
}

/** 보기 칩 + 검색(이름 · 코드 · 주소 · 전화). 순서는 서버 순서(표시 순서) 그대로. */
export function filterWorksites(sites: WorksiteDto[], filter: WsFilter, q: string): WorksiteDto[] {
  const needle = q.trim().toLowerCase();
  return sites.filter((s) => {
    if (filter === 'active' && !s.isActive) return false;
    if (filter === 'inactive' && s.isActive) return false;
    if (filter === 'noAddress' && hasAddress(s)) return false;
    if (!needle) return true;
    return [s.name, s.code, s.address, s.addressDetail, s.phone]
      .some((v) => (v ?? '').toLowerCase().includes(needle));
  });
}

/** 아이콘 칸 두 글자 — 코드가 있으면 코드 앞 두 글자(BUSAN → BU), 없으면 이름 앞 두 글자. */
export function initialsOf(s: Pick<WorksiteDto, 'name' | 'code'>): string {
  const fromCode = (s.code ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
  if (fromCode) return fromCode;
  return [...s.name.trim()].slice(0, 2).join('') || '—';
}

export const addressLine = (s: Pick<WorksiteDto, 'address' | 'addressDetail'>): string =>
  [s.address?.trim(), s.addressDetail?.trim()].filter(Boolean).join(' ');

/** 팀 집계 한 줄 — 옛 서버(undefined)는 null. */
export function teamsText(teams: WorksiteTeamCountDto[] | undefined): string | null {
  if (teams === undefined) return null;
  if (teams.length === 0) return '소속 팀 없음';
  return teams.map((t) => `${t.teamName ?? '팀 없음'} ${t.count}`).join(' · ');
}

/** 서버가 v3 칸을 주는가 — 칸 이름이 응답에 **있는지**로 본다(값이 null 이어도 있다). */
export const supportsExtended = (s: WorksiteDto | undefined): boolean =>
  !!s && Object.prototype.hasOwnProperty.call(s, 'businessRegistrationNo');

export const blankToNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

/** 상세 폼 — 입력 칸은 전부 글자(표시 순서도 입력 중에는 글자). */
export interface WsForm {
  name: string;
  code: string;
  sortOrder: string;
  isActive: boolean;
  postalCode: string;
  phone: string;
  address: string;
  addressDetail: string;
  timezone: string;
  managerUserId: string;
  businessRegistrationNo: string;
  subBusinessNo: string;
  holidayCountryCode: string;
}

export const EXT_KEYS = ['managerUserId', 'businessRegistrationNo', 'subBusinessNo', 'holidayCountryCode'] as const;

export function formOf(s?: WorksiteDto, nextSortOrder = 0): WsForm {
  return {
    name: s?.name ?? '',
    code: s?.code ?? '',
    sortOrder: String(s?.sortOrder ?? nextSortOrder),
    isActive: s?.isActive ?? true,
    postalCode: s?.postalCode ?? '',
    phone: s?.phone ?? '',
    address: s?.address ?? '',
    addressDetail: s?.addressDetail ?? '',
    timezone: s?.timezone ?? '',
    managerUserId: s?.managerUserId ?? '',
    businessRegistrationNo: s?.businessRegistrationNo ?? '',
    subBusinessNo: s?.subBusinessNo ?? '',
    holidayCountryCode: s?.holidayCountryCode ?? '',
  };
}

export const dirtyKeys = (form: WsForm, base: WsForm): Array<keyof WsForm> =>
  (Object.keys(form) as Array<keyof WsForm>).filter((k) => form[k] !== base[k]);

/**
 * 보낼 본문 — 기존 칸은 서버가 **보낸 값으로 통째 교체**하므로 늘 전부 보낸다(빈 글자는 null).
 * v3 칸은 서버가 **안 보내면 유지**하므로 바뀐 것만 보낸다(만들 때는 채운 것만) · 서버가 v3 를 모르면 아예 안 보낸다
 * (옛 서버 스키마는 additionalProperties:false 라 400 이 아니라 **조용히 지운다** — 저장된 척하지 않게 칸을 잠그고 안 보낸다).
 * `isHeadOffice` 는 보내지 않는다 — 본사는 회사정보다.
 */
export function bodyFromForm(form: WsForm, base: WsForm | null, ext: boolean): WorksiteInput {
  const body: WorksiteInput = {
    name: form.name.trim(),
    code: blankToNull(form.code),
    postalCode: blankToNull(form.postalCode),
    address: blankToNull(form.address),
    addressDetail: blankToNull(form.addressDetail),
    phone: blankToNull(form.phone),
    timezone: blankToNull(form.timezone),
    isActive: form.isActive,
    sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
  };
  if (ext) {
    for (const k of EXT_KEYS) {
      const changed = base ? form[k] !== base[k] : form[k].trim() !== '';
      if (changed) body[k] = blankToNull(form[k]);
    }
  }
  return body;
}

/** 목록에서 받은 값 그대로 한 칸만 바꿔 PATCH 할 본문(표시 순서 바꾸기). */
export function inputOf(s: WorksiteDto, patch: Partial<WorksiteInput> = {}): WorksiteInput {
  return {
    name: s.name, code: s.code, postalCode: s.postalCode, address: s.address, addressDetail: s.addressDetail,
    phone: s.phone, timezone: s.timezone, isActive: s.isActive, sortOrder: s.sortOrder,
    ...patch,
  };
}

export const BRN_PATTERN = /^\d{3}-?\d{2}-?\d{5}$/;
export const SUB_BIZ_PATTERN = /^\d{4}$/;

/** 저장 전 거르기 — 서버도 400 으로 막지만 먼저 알린다. */
export function validateForm(form: WsForm, ext: boolean): string | null {
  if (!form.name.trim()) return '사업장 이름을 입력하세요.';
  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 0 || order > 9999) return '표시 순서는 0 부터 9999 사이의 정수입니다.';
  if (ext && form.businessRegistrationNo.trim() && !BRN_PATTERN.test(form.businessRegistrationNo.trim())) {
    return '사업자등록번호는 000-00-00000 형식입니다.';
  }
  if (ext && form.subBusinessNo.trim() && !SUB_BIZ_PATTERN.test(form.subBusinessNo.trim())) {
    return '종사업장 번호는 숫자 네 자리입니다.';
  }
  return null;
}

/** 입력률 — 이름 · 코드 · 우편번호 · 주소 · 상세 주소 · 전화(+ 서버가 주면 담당자 · 사업자등록번호). */
export function missingFields(form: WsForm, ext: boolean): string[] {
  const cells: Array<[string, string]> = [
    ['사업장 이름', form.name], ['코드', form.code], ['우편번호', form.postalCode],
    ['주소', form.address], ['상세 주소', form.addressDetail], ['전화', form.phone],
    ...(ext ? ([['담당자', form.managerUserId], ['사업자등록번호', form.businessRegistrationNo]] as Array<[string, string]>) : []),
  ];
  return cells.filter(([, v]) => !v.trim()).map(([label]) => label);
}

export function fillRate(form: WsForm, ext: boolean): number {
  const total = ext ? 8 : 6;
  return Math.round(((total - missingFields(form, ext).length) / total) * 100);
}

/** 시간대 고르개 — 서버는 IANA 정식 이름 · UTC 만 받는다. 빈 값 = 회사 시간대. */
export const TIMEZONES: Array<[string, string]> = [
  ['', '회사 시간대'],
  ['Asia/Seoul', 'Asia/Seoul (서울)'],
  ['UTC', 'UTC'],
  ['Asia/Tokyo', 'Asia/Tokyo (도쿄)'],
  ['Asia/Shanghai', 'Asia/Shanghai (상하이)'],
  ['Asia/Ho_Chi_Minh', 'Asia/Ho_Chi_Minh (호찌민)'],
  ['Asia/Singapore', 'Asia/Singapore (싱가포르)'],
  ['America/New_York', 'America/New_York (뉴욕)'],
  ['America/Los_Angeles', 'America/Los_Angeles (로스앤젤레스)'],
  ['Europe/London', 'Europe/London (런던)'],
  ['Europe/Berlin', 'Europe/Berlin (베를린)'],
];

/** 휴일 달력(나라) — 빈 값 = 회사 기본(KR) 공휴일. */
export const HOLIDAY_COUNTRIES: Array<[string, string]> = [
  ['', '회사 기본 (대한민국 공휴일)'],
  ['KR', '대한민국'], ['US', '미국'], ['JP', '일본'], ['CN', '중국'],
  ['VN', '베트남'], ['SG', '싱가포르'], ['GB', '영국'], ['DE', '독일'],
];

/** 목록에 없는 값이 서버에 있으면 그 값도 고를 수 있게 앞에 붙인다(조용히 바뀌지 않게). */
export function withCurrent(options: Array<[string, string]>, current: string): Array<[string, string]> {
  return !current || options.some(([v]) => v === current) ? options : [[current, current], ...options];
}

export const WS_ERRORS: Record<string, string> = {
  FORBIDDEN: '사업장을 고치려면 근태 관리 권한이 있어야 합니다.',
  DUPLICATE_NAME: '같은 이름의 사업장이 있습니다.',
  NOT_FOUND: '사업장을 찾을 수 없습니다.',
  USER_NOT_FOUND: '없는 직원이 섞여 있습니다.',
  INVALID_TIMEZONE: '시간대 이름이 올바르지 않습니다.',
  INVALID_BUSINESS_REGISTRATION_NO: '사업자등록번호는 000-00-00000 형식입니다.',
  INVALID_SUB_BUSINESS_NO: '종사업장 번호는 숫자 네 자리입니다.',
  INVALID_HOLIDAY_COUNTRY: '휴일 달력 나라 코드가 올바르지 않습니다.',
  MANAGER_NOT_FOUND: '담당자는 우리 회사의 재직 직원이어야 합니다(퇴사 · 비활성 직원은 고를 수 없습니다).',
};

export function errorText(err: unknown, fallback: string): string {
  const res = (err as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  const code = res?.data?.code ?? '';
  if (WS_ERRORS[code]) return WS_ERRORS[code]!;
  if (res?.status === 400) return '입력 형식을 확인하세요.';
  if (res?.status === 403) return WS_ERRORS.FORBIDDEN!;
  return fallback;
}

export const FIELD_LABEL: Record<string, string> = {
  name: '이름', code: '코드', postalCode: '우편번호', address: '주소', addressDetail: '상세 주소', phone: '전화',
  timezone: '시간대', isActive: '사용', sortOrder: '표시 순서', isHeadOffice: '본사 여부',
  managerUserId: '담당자', businessRegistrationNo: '사업자등록번호', subBusinessNo: '종사업장 번호', holidayCountryCode: '휴일 달력',
};

const valueText = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '없음';
  if (typeof v === 'boolean') return v ? '예' : '아니오';
  return String(v);
};

/** 이력 제목 — 옮김 · 만듦 · 한 칸 변경 · 여러 칸 변경. */
export function historyTitle(e: WorksiteHistoryEntryDto): string {
  // 계약의 kind 가 있으면 그것으로 고른다 — 없으면(초안 서버) eventType · 칸 모양으로 가늠한다.
  if (e.kind === 'moved' || (!e.kind && e.eventType === 'hr.worksite.assigned')) return `직원 ${e.movedCount ?? 0}명 옮겨 옴`;
  if (e.kind === 'created') return '사업장 만듦';
  const changes = e.changes ?? [];
  if (!e.kind && changes.length > 0 && changes.some((c) => c.field === 'name') && changes.every((c) => c.before === null || c.before === undefined)) {
    return '사업장 만듦';
  }
  if (changes.length === 1) return `${FIELD_LABEL[changes[0]!.field] ?? changes[0]!.field} 변경`;
  if (changes.length > 1) return `${changes.length}개 칸 변경`;
  return '사업장 저장';
}

/** 이력 둘째 줄 — 누가 · 무엇이 어떻게(앞 세 칸까지). */
export function historyDetail(e: WorksiteHistoryEntryDto, personName?: (userId: string) => string | undefined): string {
  // 담당자 칸의 전 · 후는 id 문자열이다(계약) — 목록의 people 에서 이름을 찾아 보인다.
  const shown = (field: string, v: unknown) =>
    (field === 'managerUserId' && typeof v === 'string' && personName?.(v)) || valueText(v);
  const parts = (e.changes ?? []).slice(0, 3)
    .map((c) => `${FIELD_LABEL[c.field] ?? c.field} ${shown(c.field, c.before)} → ${shown(c.field, c.after)}`);
  const more = (e.changes?.length ?? 0) > 3 ? ` 외 ${(e.changes?.length ?? 0) - 3}칸` : '';
  return [e.actorName ?? '알 수 없음', parts.join(' · ') + more].filter(Boolean).join(' · ');
}

/** `YYYY-MM-DD HH:mm`(사용자 시계). 못 읽으면 원문. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const formatDate = (iso: string | null | undefined): string => formatDateTime(iso).slice(0, 10);
